'use client'

import { Link, usePathname, useRouter } from "@/locales/routing"
import { useUserStore } from "@/store/useUserStore"
import { PropsWithClassname } from "@/types"
import { DarkButton, LightButton } from "@/ui/Button"
import { AlignLeft } from "@/ui/icons/AlignLeft"
import { ChevronLeft } from "@/ui/icons/ChevronLeft"
import { OutlineHome2 } from "@/ui/icons/OutlineHome2"
import { Search } from "@/ui/icons/Search"
import { Button, cn, Input } from "@heroui/react"
import { useTranslations } from "next-intl"
import { useParams } from "next/navigation"
import { FormEventHandler, KeyboardEventHandler, memo, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, useTransition } from "react"
import dynamic from "next/dynamic"
import { MoreVertical } from "@/ui/icons/MoreVertical"
import EnergyButton from "./EnergyButton"
import { LangurageSelect } from "./LangurageSelect"
import { isLikeChatLayout } from "./Layout"
import { UserAction } from "./UserAction"
import { useRootLayout } from "./useRootLayout"
import GlobalLoading from "@/components/GlobalLoading"
import { ThemeToggle } from "@/components/ThemeToggle"
import { useShare } from "@/provider/ShareProvider"
import { ROBOT_SHARE, SHARE_METHOD_MANUAL } from "@/vendor/firebase/analytics/constant"
import { useFirebaseAnalytics } from "@/vendor/firebase/analytics"

const btnStyle = "h-9 min-w-0 px-0 sm:h-10 sm:px-[20px] text-[16px] sm:w-fit"

export const circleButtonStyle = 'min-w-10 h-10 bg-surface-2 rounded-full'

const robotMenuTriggerClass = cn(circleButtonStyle, "!border-0 hidden group-data-[state=robot]:flex")

// 与 MoreDropdown 的触发按钮外观一致的轻量占位
const RobotMenuTrigger = ({ onPress }: { onPress?: () => void }) => (
  <Button
    radius="full"
    variant="bordered"
    isIconOnly
    aria-label="More"
    isDisabled={!onPress}
    className={cn("border-[1px] border-theme-black/5 ", robotMenuTriggerClass, "data-[disabled=true]:opacity-100")}
    onPress={onPress}
  >
    <MoreVertical />
  </Button>
)

// 详情页头部的"更多"菜单（分享/举报）依赖举报弹窗、表单库等，首屏求值约百毫秒级（4x 降速），
// 先渲染同款轻量按钮，首次点击时再加载并直接展开
const RobotDropdown = dynamic(
  () => import("../(chats)/chats/_components/RobotAside/RobotHeader/RobotDropdown").then(m => m.RobotDropdown),
  { ssr: false, loading: () => <RobotMenuTrigger /> },
)

// memo：Layout 因布局 store 变化重渲染时不连带重建整个头部
export const Header = memo(({ className, searchRef, ssrLogined = false }: PropsWithClassname & { searchRef?: RefObject<HTMLInputElement>, ssrLogined?: boolean }) => {
  const router = useRouter()
  const pathname = usePathname()
  const [setAsideWrapper, setHeader] = useRootLayout(state => [state.setAsideWrapper, state.setHeader])
  const t = useTranslations()
  const ref = useRef<HTMLInputElement>(null!)
  const params = useParams()
  const [searchVal, setSearchVal] = useState(decodeURI(params.search_key as string || ''))

  useLayoutEffect(() => {
    setSearchVal(decodeURI(params.search_key as string || ''))
  }, [params.search_key])

  const mainPath = useMemo(() => {
    return pathname.split('/')[1]
  }, [pathname])

  // 服务端快照用 cookie 推断的登录态（与 SSR 一致，避免 hydration 不匹配），之后读 store 的真实登录态。
  // cookie 与 localStorage 几乎总是一致，此时 hydration 后不会再整块重渲染头部；
  // 也不需要骨架屏过渡，避免"骨架 → 按钮/头像"的布局偏移(CLS)
  const isLogined = useSyncExternalStore(
    useUserStore.subscribe,
    () => useUserStore.getState().isLogined,
    () => ssrLogined,
  )

  useEffect(() => {
    if (searchRef)
      searchRef.current = ref.current as any
  }, [])

  useLayoutEffect(() => {
    if (mainPath !== 'search') {
      ref.current?.blur()
    }
  }, [mainPath])

  const onSearchFocus = () => {
    if (!pathname.includes('search')) {
      router.push('/search')
    }
  }

  // 地址变化，控制header的状态
  useEffect(() => {
    // 取消搜索框的聚焦
    if (!pathname.includes('search')) searchRef?.current?.blur()
    // 设置header的状态
    if (pathname === '/' || pathname.includes('profile') || pathname.includes('pricing') || pathname.includes('bonus') || pathname.includes('memory')) {
      setHeader('default')
    }
    else if (isLikeChatLayout(pathname)) {
      setHeader('hidden')
    }
    else {
      setHeader('outline')
    }
  }, [pathname])

  const [isPending, startTransition] = useTransition()

  const doSearch = (raw: string) => {
    const value = (raw || '').trim()
    if (value === '') return
    // 在搜索页修改搜索词时保留当前 tab（群组/作者）
    let suffix = ''
    try {
      const tab = new URL(window.location.href).searchParams.get('tab')
      if (tab) suffix = `?tab=${tab}`
    } catch { }
    // 已在搜索页时用 replace，避免历史堆积一串搜索结果页（浏览器返回直接回上一级/主页）
    const navigate = mainPath === 'search' ? router.replace : router.push
    startTransition(() => {
      navigate(`/search/${value}${suffix}`)
    })
  }

  // 表单提交：移动端「前往/搜索」按钮最可靠的触发方式
  const onSearchSubmit: FormEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault()
    ref.current?.blur()
    doSearch(searchVal)
  }

  // 兼容部分浏览器/输入法：用 key 判断并跳过输入法合成态
  const onSearchKeyDown: KeyboardEventHandler<HTMLInputElement> = (ev) => {
    const isEnter = ev.key === 'Enter' || ev.code === 'Enter' || ev.code === 'NumpadEnter'
    if (isEnter && !(ev.nativeEvent as any)?.isComposing && (ev as any).keyCode !== 229) {
      ev.preventDefault()
      doSearch(ev.currentTarget.value)
    }
  }

  // 详情页/搜索页上被 CSS 隐藏的区块（搜索框、右侧操作区）直接不渲染，
  // 省去其中 Input、主题切换、语言选择等组件的首屏渲染与 hydration
  const isRobot = mainPath === 'robot'
  const hideActions = isRobot || mainPath === 'search'

  const showBack = useMemo(() => {
    if (['search', 'robot'].includes(mainPath)) {
      return true
    }
    return false
  }, [mainPath])


  // share 
  const { share } = useShare()

  const [shareUrl, setShareUrl] = useState('/')

  useEffect(() => {
    setShareUrl(`${location.origin}/${params.locale}/robot/${params.id}`)
  }, [params.id, params.locale])
  const { logShare } = useFirebaseAnalytics()
  const [robotMenuActivated, setRobotMenuActivated] = useState(false)
  // 真正的菜单带 defaultOpen（首次点击后直接展开），离开详情页时须复位，
  // 否则再次进入详情页重新挂载时会自动弹出
  useEffect(() => {
    if (mainPath !== 'robot') setRobotMenuActivated(false)
  }, [mainPath])

  const onShare = () => {
    share({
      url: shareUrl,
    })
    logShare({
      method: SHARE_METHOD_MANUAL,
      content_type: ROBOT_SHARE,
      item_id: params.id
    })
  }


  return (
    <>
      {isPending && <GlobalLoading />}
      <header
        data-state={mainPath}
        className={cn(
          "bg-surface items-center  w-full box-border",
          "flex gap-3 sm:gap-[20px] flex-1 max-w-full data-[state=search]:max-w-[1190px] transition-all",
          "data-[state=robot]:max-w-[1190px]",
          'group',
          className
        )}>


        <div className="flex-1 gap-[8px]">
          <div data-state={mainPath} className={
            cn("mx-w-[600px] 2xl:max-w-[70%] flex items-center gap-[8px]",
              "data-[state=search]:max-w-full w-full transition-width "
            )}>
            <Button
              isIconOnly
              className={cn(
                circleButtonStyle,
                "hidden group-data-[state=search]:flex group-data-[state=robot]:flex",
              )}
              onPress={() => mainPath === 'search' ? router.push('/') : router.back()}
            >
              <ChevronLeft />
            </Button>

            <Button
              isIconOnly
              className={cn(
                circleButtonStyle,
                "hidden group-data-[state=robot]:flex",
              )}
              onPress={() => router.push('/')}
            >
              <OutlineHome2 />
            </Button>

            {!showBack && <Button
              isIconOnly
              data-show={!showBack}
              className={cn(circleButtonStyle,
                "flex justify-center h-9 w-9 min-w-0 sm:h-10 sm:w-10 items-center bg-transparent",
                "hidden data-[show=true]:flex lg:!hidden"
              )}
              onPress={() => setAsideWrapper('default')}
            >
              <AlignLeft />
            </Button>}

            {!isRobot && <form
              onSubmit={onSearchSubmit}
              className="hidden group-data-[state=search]:flex sm:flex flex-1"
            >
              <Input
                ref={ref}
                className="search-input w-full flex-1"
                enterKeyHint="search"
                placeholder={t('header.search.placeholder')}
                labelPlacement="outside"
                classNames={{
                  inputWrapper: "h-10 min-h-[auto] bg-gray-bg group-data-[state=robot]:hidden group-data-[state=bonus]:hidden",
                }}
                value={searchVal}
                onValueChange={setSearchVal}
                onFocus={onSearchFocus}
                radius="full"
                onKeyDown={onSearchKeyDown}
                startContent={<Search />}
              />
            </form>}
          </div>
        </div>

        <div className="hidden group-data-[state=robot]:flex">

          <LangurageSelect />
        </div>

        {mainPath === 'robot' && (robotMenuActivated ?
          <RobotDropdown
            defaultOpen
            onShare={onShare}
            robotId={params.id as string}
            triggerClass={robotMenuTriggerClass}
          /> :
          <RobotMenuTrigger onPress={() => setRobotMenuActivated(true)} />
        )}


        {!hideActions && <div
          className={cn(
            "flex gap-2 sm:gap-[12px] items-center justify-end",
            "group-data-[state=search]:hidden group-data-[state=robot]:hidden"
          )}>

          {
            isLogined && <EnergyButton />
          }

          <Button onPress={onSearchFocus}
            className="rounded-full h-9 w-9 min-w-0 flex sm:hidden bg-gray-bg flex-shrink-0" isIconOnly>
            <Search />
          </Button>
          <ThemeToggle />
          <LangurageSelect />
          {
            isLogined ?
              <UserAction
                triggerRadius="full"
                triggerClass="!p-2 !pr-4 bg-gray-bg"
                className=" h-10 leading-[1]" header /> :
              <div className="flex gap-2 sm:gap-[12px]">
                <DarkButton
                  as={Link}
                  href={`/login/sign-up`}
                  className={cn(
                    "hidden sm:flex",
                    btnStyle
                  )}>{t('header.signUp')}</DarkButton>
                <LightButton
                  as={Link}
                  href={`/login`}
                  className="h-9 min-w-0 sm:h-10 sm:px-[20px] text-[16px] sm:w-fit"
                >{t('header.login')}</LightButton>
              </div>
          }

        </div>}
      </header>
    </>

  )
})
